import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { useGlobalModalStore } from '@/stores/useGlobalModalStore/useGlobalModalStore'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useNavigate } from '@tanstack/react-router'
import { ReportType } from '@/hooks'

type DeleteReportContentProps = {
    reportId: string
    reportName: string
    type?: ReportType
}

export const DeleteReportContent = ({
    reportId,
    reportName,
    type,
}: DeleteReportContentProps) => {
    const { t } = useTranslation('translations')
    const addAlert = useToastStore((state) => state.addAlert)
    const setModalState = useGlobalModalStore((state) => state.setModalState)
    const { useDeleteReportMutation } = useReportsApi()
    const { mutate: deleteReportMutation, isPending } =
        useDeleteReportMutation()
    const navigate = useNavigate()

    const closeModal = () => {
        setModalState({ isOpen: false })
    }

    const handleDelete = () => {
        deleteReportMutation(reportId, {
            onSuccess: () => {
                closeModal()

                if (type) {
                    void navigate({
                        to: '/reporting',
                        search: {
                            page: 1,
                            recordsPerPage: 10,
                            sortBy: 'updatedAt',
                            order: 'desc',
                        },
                    })
                }

                addAlert({
                    variant: 'success',
                    title: t('reporting.delete_report.success_toast.title'),
                    description: t(
                        'reporting.delete_report.success_toast.description',
                        {
                            reportName,
                        }
                    ),
                })
            },
            onError: () => {
                closeModal()
                addAlert({
                    variant: 'error',
                    title: t('reporting.delete_report.error_toast.title'),
                    description: t(
                        'reporting.delete_report.error_toast.description'
                    ),
                })
            },
        })
    }
    const deleteText = isPending
        ? t('reporting.delete_report.loading_delete_button')
        : t('reporting.delete_report.delete_button')

    return (
        <div className="text-sm">
            <div className="flex flex-row-reverse gap-4 border-t border-t-neutral-200 px-6 py-4">
                <Button
                    name="delete"
                    variant={'solidPrimaryDestructive'}
                    size="large"
                    onClick={handleDelete}
                    isLoading={isPending}
                    disabled={isPending}
                    className="whitespace-nowrap"
                    data-testid="delete-confirm-button"
                >
                    {deleteText}
                </Button>
                <Button
                    name="cancel"
                    variant={'outlinePrimary'}
                    size="large"
                    onClick={closeModal}
                    disabled={isPending}
                    className="whitespace-nowrap"
                    data-testid="cancel-button"
                >
                    {t('reporting.delete_report.cancel_button')}
                </Button>
            </div>
        </div>
    )
}
